// ZuschauerAnsicht.jsx — Live-Ansicht für Zuschauer und Hallenschirm (Aufgabe 178, 02.10.2026)
//
// Durchsicht mit Sascha: die alte Ansicht (LiveSpectatorDashboard) zeigte keine
// Matte, kein Rot/Blau, „--:--“-Platzhalter, interne Kampfnummern, englische
// Status, war am Handy abgeschnitten, auf dem Beamer zu klein — und lag nur im
// Admin-Dashboard. Jetzt eine Ansicht im Leinwand-Design (styles/leinwand.css),
// genutzt vom öffentlichen Hallenschirm /zuschauer/:turnierId und vom Dashboard.
//
// Daten: alle 5 s GET /api/brackets/turnier/:id/live (öffentlich; Minderjährige
// schon gekürzt; laufende Kämpfe mit Live-Stand der Kampfrichter-Tablets) —
// sofort neu bei Socket-Ereignis kampf_finished. Nach jedem neu beendeten
// Kampf: 5 s Ergebnis, nach 20 s die Rangliste der Kategorie für 15 s
// (Ablauf aus Aufgabe 175 übernommen).
//
// ⛔ Keine Emoji (Leinwand-Regel, CLAUDE.md): sie sehen auf jedem Beamer anders aus.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import '../../styles/leinwand.css';
import './ZuschauerAnsicht.css';
import { useAnimierteListe } from '../../hooks/useHervorheben';
import { standText } from '../../lib/minuspunkte';
import { kurzzeichen, kurzzeichenText } from '../../lib/verlaengerung';

const name = (v, n) => `${v || ''} ${n || ''}`.trim() || 'offen';
const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
const RUNDE = (r) => (r ? `Runde ${r}` : '');

function aufbereiten(k) {
  const sieger1 = k.gewinner_id && Number(k.gewinner_id) === Number(k.kaempfer1_id);
  return {
    id: k.kampf_id,
    kategorie: k.bracket_name || k.division_code,
    code: k.division_code,
    runde: k.runde,
    // Beendet: die tatsächlich gekämpfte Matte (confirm-result schreibt sie);
    // laufend: die Matte des Tablets; sonst die geplante aus dem Zeitplan.
    matte: k.live_matte ?? (k.status === 'finished' ? k.gekaempft_matte : null) ?? k.kat_matte ?? k.tatami_nummer ?? null,
    uhrzeit: k.kat_uhrzeit || null,
    // punkte = Stand nach Abzug der Minuspunkte (20.10.2026), darf negativ sein
    a: { name: name(k.k1_vorname, k.k1_nachname), verein: k.k1_verein, punkte: k.punkte_kaempfer1 ?? 0, verw: k.verwarnungen1 || 0, minus: Number(k.minuspunkte_kaempfer1) || 0 },
    b: { name: name(k.k2_vorname, k.k2_nachname), verein: k.k2_verein, punkte: k.punkte_kaempfer2 ?? 0, verw: k.verwarnungen2 || 0, minus: Number(k.minuspunkte_kaempfer2) || 0 },
    rotSeite: k.rot_seite ?? null,           // 1 = erster Kämpfer ist Rot, 2 = zweiter, null = unbekannt
    live: !!k.live, restzeit: k.live_restzeit ?? null, uhrLaeuft: !!k.live_laeuft, abgerufen: Date.now(),
    sieger: k.gewinner_id ? (sieger1 ? 'a' : 'b') : null,
    beendetUm: k.beendet_um || null,
    // „n. V.“, „GP“ (Goldener Punkt), „KE“ (Kampfrichterentscheid) hinter dem Ergebnis
    nachVerlaengerung: kurzzeichen(k.verlaengerungen, k.win_method),
  };
}

function Restzeit({ k }) {
  const [, setTakt] = useState(0);
  useEffect(() => {
    if (!k.uhrLaeuft) return undefined;
    const t = setInterval(() => setTakt((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [k.uhrLaeuft]);
  if (!k.live || k.restzeit == null) return null;
  const rest = Math.max(0, k.restzeit - (k.uhrLaeuft ? Math.floor((Date.now() - k.abgerufen) / 1000) : 0));
  return <div className={`zs-uhr ${k.uhrLaeuft ? 'laeuft' : ''}`}>{mmss(rest)}{k.uhrLaeuft ? '' : ' · Pause'}</div>;
}

function Seite({ s, farbe, sieger }) {
  return (
    <div className={`zs-seite ${farbe || ''} ${sieger ? 'sieger' : ''}`}>
      {farbe && <span className="zs-farbe">{farbe === 'rot' ? 'ROT' : 'BLAU'}</span>}
      <span className="zs-seite-name">{s.name}</span>
      {s.verein && <span className="zs-seite-verein">{s.verein}</span>}
    </div>
  );
}

function LaufenderKampf({ k, klasse = '', flip }) {
  const farbeA = k.rotSeite === 1 ? 'rot' : k.rotSeite === 2 ? 'blau' : null;
  const farbeB = k.rotSeite === 1 ? 'blau' : k.rotSeite === 2 ? 'rot' : null;
  return (
    <article className={`zs-karte ${klasse}`} data-flip={flip}>
      <header className="zs-karte-kopf">
        <span className="zs-matte">{k.matte ? `MATTE ${k.matte}` : 'MATTE'}</span>
        <span className="zs-karte-kat">{k.kategorie}{k.runde ? ` · ${RUNDE(k.runde)}` : ''}</span>
      </header>
      <div className="zs-karte-mitte">
        <Seite s={k.a} farbe={farbeA} />
        <div className="zs-stand">
          <div className="zs-punkte">{standText(k.a.punkte)}<span>:</span>{standText(k.b.punkte)}</div>
          {(k.a.minus > 0 || k.b.minus > 0) && (
            <div className="zs-minus" title="Minuspunkte">
              <span>{k.a.minus > 0 ? standText(-k.a.minus) : ''}</span>
              <span>{k.b.minus > 0 ? standText(-k.b.minus) : ''}</span>
            </div>
          )}
          <Restzeit k={k} />
          {(k.a.verw > 0 || k.b.verw > 0) && <div className="zs-verw">Verwarnungen {k.a.verw} : {k.b.verw}</div>}
        </div>
        <Seite s={k.b} farbe={farbeB} />
      </div>
    </article>
  );
}

export default function ZuschauerAnsicht({ turnierId, eingebettet = false }) {
  const [turnier, setTurnier] = useState(null);
  const [laufend, setLaufend] = useState([]);
  const [naechste, setNaechste] = useState([]);
  const [beendet, setBeendet] = useState([]);
  const [verbunden, setVerbunden] = useState(true);
  const [geladen, setGeladen] = useState(false);
  const [einblendung, setEinblendung] = useState(null); // { art: 'ergebnis'|'rangliste', k, rang }
  const bekannte = useRef(null);
  const schlange = useRef([]);
  const laeuft = useRef(false);
  const ladenRef = useRef(null);
  const zeitgeber = useRef([]);

  const ranglisteLaden = useCallback(async (code) => {
    try {
      const r = await fetch(`/api/siegerehrung/turnier/${turnierId}/public`, { cache: 'no-store' });
      const d = await r.json();
      // ⚠️ Die Antwort trägt die Kategorien in `data` (nicht `kategorien` — so las
      // es die alte Ansicht, und die Rangliste kam nie aus der Siegerehrung).
      const kat = (d?.data || []).find((x) => x.division_code === code);
      return kat?.plaetze?.length ? kat.plaetze.filter((p) => p.platzierung >= 1).sort((a, b) => a.platzierung - b.platzierung) : null;
    } catch { return null; }
  }, [turnierId]);

  const naechsteEinblendung = useCallback(() => {
    if (laeuft.current) return;
    const k = schlange.current.shift();
    if (!k) return;
    laeuft.current = true;
    setEinblendung({ art: 'ergebnis', k });
    zeitgeber.current.push(setTimeout(() => setEinblendung(null), 5000));
    zeitgeber.current.push(setTimeout(async () => {
      const rang = await ranglisteLaden(k.code);
      if (rang) setEinblendung({ art: 'rangliste', k, rang });
      zeitgeber.current.push(setTimeout(() => { setEinblendung(null); laeuft.current = false; naechsteEinblendung(); }, rang ? 15000 : 0));
    }, 20000));
  }, [ranglisteLaden]);

  useEffect(() => {
    if (!turnierId) return undefined;
    let aktiv = true;
    const laden = async () => {
      try {
        const r = await fetch(`/api/brackets/turnier/${turnierId}/live`, { cache: 'no-store' });
        if (!aktiv) return;
        if (!r.ok) { setVerbunden(false); return; }
        const d = await r.json();
        if (!d.success) return;
        setVerbunden(true);
        setGeladen(true);
        setTurnier(d.turnier || null);
        setLaufend((d.laufend || []).map(aufbereiten).sort((x, y) => (x.matte ?? 99) - (y.matte ?? 99)));
        setNaechste((d.naechste || []).map(aufbereiten));
        const fertig = (d.beendet || []).map(aufbereiten);
        setBeendet(fertig);
        if (!bekannte.current) { bekannte.current = new Set(fertig.map((k) => k.id)); return; }
        const neu = fertig.filter((k) => !bekannte.current.has(k.id)).reverse();
        neu.forEach((k) => { bekannte.current.add(k.id); schlange.current.push(k); });
        if (neu.length) naechsteEinblendung();
      } catch { if (aktiv) setVerbunden(false); }
    };
    ladenRef.current = laden;
    laden();
    const t = setInterval(laden, 5000);
    // Kampfende sofort: der Server meldet global-update/kampf_finished
    const sock = io(window.location.origin, { transports: ['websocket', 'polling'] });
    sock.on('global-update', (u) => { if (u?.type === 'kampf_finished') ladenRef.current?.(); });
    const zg = zeitgeber.current;
    return () => { aktiv = false; clearInterval(t); sock.disconnect(); zg.forEach(clearTimeout); };
  }, [turnierId, naechsteEinblendung]);

  // To-do 496: Ein Kampf, der neu auf eine Matte geht, blendet ein und
  // leuchtet kurz auf; ein beendeter blendet aus. Punkte und Uhr zählen
  // nicht als Änderung (sonst leuchtete es bei jedem Treffer).
  const laufendAnim = useAnimierteListe(geladen ? laufend : null, (k) => k.id, () => '', { neueMarkieren: true });
  // Aufrufliste „Als Nächstes“: neue Paarungen blenden ein, aufgerufene aus,
  // die übrigen rücken nach. Ändert sich die Matte oder Uhrzeit, leuchtet es.
  const naechsteAnim = useAnimierteListe(geladen ? naechste.slice(0, 10) : null, (k) => k.id,
    (k) => `${k.matte ?? ''}|${k.uhrzeit ?? ''}|${k.a.name}|${k.b.name}`);
  // Ergebnisliste „Zuletzt beendet“: ein neues Ergebnis blendet ein und
  // leuchtet kurz, die älteren rutschen nach unten.
  const beendetAnim = useAnimierteListe(geladen ? beendet.slice(0, 12) : null, (k) => k.id,
    (k) => `${k.sieger ?? ''}|${k.a.punkte}:${k.b.punkte}`, { neueMarkieren: true });

  const unterzeile = [turnier?.datum_text, turnier?.ort].filter(Boolean).join(' · ');

  return (
    <div className={`lw mit-leiste ohne-fuss zs ${eingebettet ? 'zs-eingebettet' : ''}`}>
      <header className="lw-kopf">
        <div className="lw-kopf-links">
          <div className="lw-kopf-turnier">{turnier?.name || 'Turnier'}</div>
          {unterzeile && <div className="lw-kopf-kat">{unterzeile}</div>}
        </div>
        <div className={`lw-kopf-status ${verbunden ? '' : 'wartet'}`}>{verbunden ? '● LIVE' : 'Verbindung …'}</div>
      </header>

      <main className="lw-buehne zs-buehne">
        <h2 className="zs-titel">Jetzt auf den Matten</h2>
        {laufendAnim.zeilen.length === 0
          ? <p className="zs-leer">Gerade läuft kein Kampf.</p>
          : (
            <div className="zs-raster" ref={laufendAnim.behaelterRef}>
              {laufendAnim.zeilen.map((z) => (
                <LaufenderKampf key={z.schluessel} k={z.eintrag} flip={z.schluessel} klasse={laufendAnim.klasse(z)} />
              ))}
            </div>
          )}

        <h2 className="zs-titel">Als Nächstes</h2>
        {naechsteAnim.zeilen.length === 0 ? <p className="zs-leer">Keine weiteren Kämpfe angesetzt.</p> : (
          <div className="zs-liste" ref={naechsteAnim.behaelterRef}>
            {naechsteAnim.zeilen.map((z) => { const k = z.eintrag; return (
              <div key={z.schluessel} data-flip={z.schluessel} className={`zs-zeile ${naechsteAnim.klasse(z)}`}>
                <span className="zs-zeile-matte">{k.matte ? `Matte ${k.matte}` : ''}</span>
                <span className="zs-zeile-zeit">{k.uhrzeit ? `ca. ${k.uhrzeit}` : ''}</span>
                <span className="zs-zeile-kat">{k.kategorie}{k.runde ? ` · ${RUNDE(k.runde)}` : ''}</span>
                <span className="zs-zeile-paar">
                  <span className="zs-paar-name">{k.a.name}</span>
                  <span className="zs-gegen">gegen</span>
                  <span className="zs-paar-name">{k.b.name}</span>
                </span>
              </div>
            ); })}
          </div>
        )}

        {einblendung && (
          <div className="zs-einblendung">
            {einblendung.art === 'ergebnis' ? (
              <>
                <div className="zs-ein-kat">{einblendung.k.kategorie}{einblendung.k.matte ? ` · Matte ${einblendung.k.matte}` : ''}</div>
                <div className="zs-ein-lbl">Sieger</div>
                <div className="zs-ein-name">{einblendung.k[einblendung.k.sieger || 'a'].name}</div>
                {einblendung.k[einblendung.k.sieger || 'a'].verein && <div className="zs-ein-verein">{einblendung.k[einblendung.k.sieger || 'a'].verein}</div>}
                <div className="zs-ein-stand">{standText(einblendung.k.a.punkte)} : {standText(einblendung.k.b.punkte)}{einblendung.k.nachVerlaengerung ? <>{' '}<abbr title={kurzzeichenText(einblendung.k.nachVerlaengerung)}>{einblendung.k.nachVerlaengerung}</abbr></> : ''}</div>
              </>
            ) : (
              <>
                <div className="zs-ein-kat">Platzierungen · {einblendung.k.kategorie}</div>
                <div className="zs-ein-rang">
                  {einblendung.rang.slice(0, 8).map((p, i) => (
                    <div key={i} className={`zs-rang-zeile platz-${p.platzierung}`}>
                      <span className="zs-rang-platz">{p.platzierung}.</span>
                      <span className="zs-rang-name">{name(p.vorname, p.nachname)}</span>
                      <span className="zs-rang-verein">{p.verein_name || ''}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </main>

      <aside className="lw-leiste">
        <div className="lw-leiste-titel">Zuletzt beendet</div>
        {beendetAnim.zeilen.length === 0 ? <p className="zs-leer klein">Noch kein Kampf beendet.</p> : (
          <div ref={beendetAnim.behaelterRef}>
            {beendetAnim.zeilen.map((z) => { const k = z.eintrag; return (
          <div key={z.schluessel} data-flip={z.schluessel} className={`zs-fertig ${beendetAnim.klasse(z)}`}>
            <div className="zs-fertig-kat">{k.kategorie}{k.beendetUm ? ` · ${k.beendetUm}` : ''}</div>
            <div className="zs-fertig-zeile">
              <span className="zs-fertig-name">{k.sieger ? k[k.sieger].name : '—'}</span>
              <span className="zs-fertig-stand">{standText(k.a.punkte)}:{standText(k.b.punkte)}{k.nachVerlaengerung ? <>{' '}<abbr title={kurzzeichenText(k.nachVerlaengerung)}>{k.nachVerlaengerung}</abbr></> : ''}</span>
            </div>
          </div>
            ); })}
          </div>
        )}
      </aside>
    </div>
  );
}
